// Turn the router's `dashboard_reset` decision into a confirmable reset proposal. There is NOTHING to
// interpret or ground — a reset takes no parameters — so this is a purely static confirm card. On Apply
// the shell calls the page's existing resetDashboard(), which clears ALL filters AND resets every chart's
// display state back to defaults. This is deliberately DISTINCT from a filters-only "clear the filters"
// (which routes as filter_query -> clear_all).
//
// Mounted on BOTH dashboards, which is why the copy is DERIVED rather than written. The two pages reset
// different things — the FCA Dashboard has Top N, 2D/3D and a maximized view; the Asset Dashboard has
// none of those — so a single hardcoded sentence necessarily lied on one of them, promising to undo
// controls the user has never seen. The chart properties are read from the SAME app_context the page
// already sends (the "source affordances from the surface" rule the chart capability runs on), so a third
// dashboard describes itself correctly with no edit here.
//
// The FILTERS half is fixed and unconditional: every dashboard reset clears them (on the Asset Dashboard
// that includes its map and table filters too), and it is the consequence most worth being explicit about
// on a confirm card. Only the chart half varies.

// Chart properties -> how the confirm card names them, in the order they should read. A property absent
// from this map is deliberately not mentioned: `export` is an ACTION a chart offers, not display state a
// reset puts back. Two properties can share a label (CSP groups on two axes) — the list is de-duplicated,
// so "grouping" is said once.
const PROPERTY_LABELS = [
    ["chart_type", "chart type"],
    ["category", "grouping"],
    ["display", "grouping"],
    ["sort_by", "sorting"],
    ["top_n", "Top N"],
    ["dimension", "2D/3D"],
    ["view", "maximized view"]
];

// Which display properties do this mount's charts actually have? Read from `current` UNION `options`,
// because the two carry different halves of the truth: a page declares its selectable values in `options`,
// but Top N is a free scalar with no option list and appears only in `current` (chartCatalog.SCALAR_SUPPORT).
// Taking either alone would drop a control the reset really does undo.
const chartPropertiesIn = appContext => {
    const charts = (appContext && appContext.charts) || {};
    const present = new Set();
    Object.keys(charts).forEach(key => {
        const chart = charts[key] || {};
        Object.keys(chart.current || {}).forEach(p => present.add(p));
        Object.keys(chart.options || {}).forEach(p => present.add(p));
    });
    const labels = [];
    PROPERTY_LABELS.forEach(([property, label]) => {
        if (present.has(property) && !labels.includes(label)) labels.push(label);
    });
    return labels;
};

// "a, b, and c" — the Oxford join the copy has always used.
const joinLabels = labels =>
    labels.length < 2 ? labels.join("") : `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;

// The confirm sentence for THIS mount. With no chart context (an unknown or chartless dashboard) the chart
// clause degrades to the unqualified form rather than naming controls we can't verify — the filters half is
// unchanged either way.
export const buildResetMessage = appContext => {
    const labels = chartPropertiesIn(appContext);
    const charts = labels.length ? `every chart — ${joinLabels(labels)} — ` : "every chart ";
    return `Reset the whole dashboard? This clears all the filters you've applied and puts ${charts}back to its defaults.`;
};

// interpretResponse = { request_id, session_id, intent_kind: "dashboard_reset" };
// ctx = { utterance, appContext } — appContext is the payload the mount already sends on /interpret.
export const buildResetProposal = (interpretResponse, ctx = {}) => ({
    requestId: interpretResponse && interpretResponse.request_id,
    sessionId: interpretResponse && interpretResponse.session_id,
    utterance: ctx.utterance,
    reset: true,
    message: buildResetMessage(ctx.appContext)
});
